import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TableModule } from '@openng/optimus-ui/table';
import { FacilitySignIn } from 'src/app/model/locations/facility-sign-in';
import { Facility } from 'src/app/model/locations/facility';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { DatePipe } from '@angular/common';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { TabService } from 'src/app/core/services/tab.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { PublicFacilityApiService } from 'src/app/services/api/public-facility-api.service';
import { FacilitySignInMemoryService } from 'src/app/services/facility-sign-in-memory.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-facility-visitor-log',
  imports: [
    ProgressSpinnerComponent,
    TranslateModule,
    DatePipe,
    ButtonModule,
    TableModule,
  ],
  templateUrl: './facility-visitor-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './facility-visitor-log.component.scss',
})
export class FacilityVisitorLogComponent implements OnInit, OnDestroy {
  protected config = inject(DynamicDialogConfig);
  protected ref = inject(DynamicDialogRef);
  protected translateService = inject(TranslateService);
  protected facilityApi = inject(FacilityApiService);
  protected publicFacilityApi = inject(PublicFacilityApiService);
  protected signInMemory = inject(FacilitySignInMemoryService);
  protected confirmationService = inject(ConfirmationService);
  protected logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);

  readonly gotoFacilityId = input<string | null>(null);
  readonly activeVisitors: WritableSignal<FacilitySignIn[]> = signal([]);
  readonly facilityId: WritableSignal<string> = signal('');
  readonly loading = signal(true);
  readonly facility: WritableSignal<Facility | null> = signal(null);
  readonly signingOutIds = signal<Set<string>>(new Set());
  readonly queryErrored = signal(false);

  tabClosingSub?: Subscription;

  ngOnInit(): void {
    this.loading.set(true);
    this.facilityId.set(this.config?.data?.facilityId);
    const gotoFacilityId = this.gotoFacilityId();
    if (gotoFacilityId) {
      this.facilityId.set(gotoFacilityId);
      this._tabService.updateActiveTabLabel('Facility Visitors Log');
    }
    this.facility.set(null);
    this.getSignedInPeople();
    this.getFacility();
    this.setupTabClosingSubscription();
  }

  getSignedInPeople(onSettled?: () => void): void {
    this.facilityApi.getSignedInPeople(this.facilityId()).subscribe({
      next: (response) => {
        this.activeVisitors.set(response);
        this.loading.set(false);
        onSettled?.();
      },
      error: (error) => {
        this.loading.set(false);
        this.queryErrored.set(true);
        onSettled?.();
      },
    });
  }

  requestSignOut(visitor: FacilitySignIn): void {
    if (this.isSigningOut(visitor.id) || !this.hasResolvedFacilityIdentity()) {
      return;
    }

    this.confirmationService.confirm({
      header: this.translateService.instant(
        'COMMON.LABEL.HEADERS.CONFIRMATION'
      ),
      message: this.translateService.instant(
        'LOCATION.MESSAGES.CONFIRMATION.SIGN_OUT_VISITOR',
        { name: visitor.name }
      ),
      accept: () => this.signOut(visitor),
    });
  }

  private signOut(visitor: FacilitySignIn): void {
    const facilityIdentity = this.getResolvedFacilityIdentity();
    if (this.isSigningOut(visitor.id) || !facilityIdentity) {
      return;
    }

    this.signingOutIds.update((ids) => new Set(ids).add(visitor.id));
    this.publicFacilityApi.createFacilitySignIn(visitor.id, null).subscribe({
      next: () => {
        Promise.resolve()
          .then(() =>
            this.signInMemory.remove(
              facilityIdentity.facilityUid,
              facilityIdentity.facilityId,
              visitor.id
            )
          )
          .catch((error) => this.logAndMessage.errorLogOnly(error))
          .finally(() => {
            this.logAndMessage.translateToSuccessMessage({
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
              bodyKey: 'LOCATION.MESSAGES.SUCCESS.SIGN_OUT',
            });
            this.getSignedInPeople(() => this.clearSigningOut(visitor.id));
          });
      },
      error: (error) => {
        this.clearSigningOut(visitor.id);
        this.logAndMessage.errorLogOnly(error);
        this.logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'LOCATION.MESSAGES.ERROR.SIGN_OUT',
        });
      },
    });
  }

  protected isSigningOut(visitorId: string): boolean {
    return this.signingOutIds().has(visitorId);
  }

  private clearSigningOut(visitorId: string): void {
    this.signingOutIds.update((ids) => {
      const remainingIds = new Set(ids);
      remainingIds.delete(visitorId);
      return remainingIds;
    });
  }

  protected hasResolvedFacilityIdentity(): boolean {
    return this.getResolvedFacilityIdentity() !== null;
  }

  private getResolvedFacilityIdentity(): {
    facilityUid: string;
    facilityId: string;
  } | null {
    const facility = this.facility();
    const facilityId = this.facilityId();

    if (!facility?.facilityUid || !facilityId || facility.id !== facilityId) {
      return null;
    }

    return {
      facilityUid: facility.facilityUid,
      facilityId,
    };
  }

  getFacility() {
    this.facilityApi.getFacility(this.facilityId()).subscribe({
      next: (response) => {
        this.facility.set(response);
        this._breadCrumbService.addBreadCrumb({
          label: (this.facility()?.facilityName ?? '') + ' Visitors Log',
          routerLink: null,
        });
      },
      error: (error) => {
        this.logAndMessage.errorLogOnly(error);
        this.logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'LOCATION.MESSAGES.ERROR.LOAD_FACILITIES',
        });
      },
    });
  }

  ngOnDestroy(): void {
    if (this.ref) {
      this.ref.close();
    }
  }

  setupTabClosingSubscription(): void {
    this.tabClosingSub = this._tabService.attemptCloseEvent.subscribe(
      (closingTab) => {
        if (this.gotoFacilityId()) {
          this._tabService.closeTab(closingTab.index);
        }
      }
    );
  }
}
